import { z } from 'zod';
import { DocumentTypeSchema, PatientIdSchema, SexSchema } from './common';

/**
 * Lista de EPS comunes en Colombia. No es exhaustiva: la UI debe permitir texto libre
 * validado, pero ofrecer estas como sugerencias para normalizar reportería.
 */
export const KnownEpsSchema = z.string().min(2).max(120);

export const PatientSchema = z.object({
  id: PatientIdSchema,
  tipoDoc: DocumentTypeSchema,
  documento: z
    .string()
    .min(4, 'Documento muy corto')
    .max(20, 'Documento muy largo')
    .regex(/^[A-Za-z0-9-]+$/, 'Solo alfanumérico y guiones'),
  nombres: z.string().min(2).max(80),
  apellidos: z.string().min(2).max(80),
  fechaNacimiento: z.string().date(),
  sexo: SexSchema,
  telefono: z.string().min(7).max(25).optional(),
  direccion: z.string().max(200).optional(),
  ciudad: z.string().max(80).optional(),
  eps: KnownEpsSchema.optional(),
  historiaClinica: z.string().max(40).optional(),
  alergias: z.string().max(500).optional(),
  contactoEmergencia: z.string().max(200).optional(),
  procedimientosCount: z.number().int().min(0).default(0),
});
export type Patient = z.infer<typeof PatientSchema>;

export const CreatePatientSchema = PatientSchema.omit({
  id: true,
  procedimientosCount: true,
});
export type CreatePatientInput = z.infer<typeof CreatePatientSchema>;

export const UpdatePatientSchema = CreatePatientSchema.partial();
export type UpdatePatientInput = z.infer<typeof UpdatePatientSchema>;

export const PatientSummarySchema = PatientSchema.pick({
  id: true,
  tipoDoc: true,
  documento: true,
  nombres: true,
  apellidos: true,
  sexo: true,
  eps: true,
  alergias: true,
}).extend({
  edad: z.number().int().min(0).max(130),
});
export type PatientSummary = z.infer<typeof PatientSummarySchema>;

/**
 * Credenciales del portal de resultados del paciente. Tanto el usuario como la
 * contraseña son SIEMPRE el número de documento (cédula) del paciente. No se
 * generan ni se almacenan contraseñas.
 */
export const PortalCredentialsSchema = z.object({
  usuario: z.string(),
  password: z.string(),
});
export type PortalCredentials = z.infer<typeof PortalCredentialsSchema>;
